import type { ColumnDef, Header, Table } from "@tanstack/react-table";

import { Button, Icon } from "@pepperstone/pepper-ui-kit";
import { flexRender } from "@tanstack/react-table";
import clsx from "clsx";
import { useEffect, useRef } from "react";

import { BaseArrowDownIcon } from "../Icons";

export type SortingIconRenderer<T> = (
  header: Header<T, unknown>,
) => React.ReactNode;

export type ExpandToggle<T> = {
  getExpandLabel: (row: T) => string;
  getCollapseLabel: (row: T) => string;
};

const FREEZE_SHADOW_CLASS = "shadow-[8px_0_12px_-8px_rgba(3,20,46,0.12)]";

const defaultGetSortingIcon = <T,>(header: Header<T, unknown>) => {
  if (!header.column.getCanSort()) {
    return null;
  }

  const isSorted = header.column.getIsSorted();
  const isAsc = isSorted === "asc";
  const isDesc = isSorted === "desc";

  return (
    <div data-testid="sort-icon" className="text-gray-400">
      <Icon
        icon={
          isAsc
            ? "sort-by-ascending"
            : isDesc
              ? "sort-by-descending"
              : "switch-trade"
        }
        size="tiny"
        color={isAsc || isDesc ? "black" : "inherit"}
      />
    </div>
  );
};

export type TableContentProps<T> = {
  table: Table<T>;
  columns: ColumnDef<T, any>[];
  isError?: boolean;
  cellContainerClassName?: string;
  rowClassName?: string;
  enableHorizontalScroll?: boolean;
  stickyFirstColumn?: boolean;
  highlightedRowId?: string;
  getRowId?: (row: T) => string;
  fixedLayout?: boolean;
  isScrolled?: boolean;
  insideScrollFrame?: boolean;
  getRowClassName?: (row: T) => string;
  onRowClick?: (row: T) => void;
  expandToggle?: ExpandToggle<T>;
};

export const TableContent = <T,>({
  table,
  columns,
  isError = false,
  cellContainerClassName = "",
  rowClassName,
  enableHorizontalScroll = false,
  stickyFirstColumn = false,
  highlightedRowId,
  getRowId,
  fixedLayout = false,
  isScrolled = false,
  insideScrollFrame = false,
  getRowClassName,
  onRowClick,
  expandToggle,
}: TableContentProps<T>) => {
  const rows = table.getRowModel().rows;
  const columnCount = columns.length + (expandToggle ? 1 : 0);

  const stickyColumn = stickyFirstColumn && enableHorizontalScroll;

  const highlightedRowKey =
    highlightedRowId && getRowId
      ? rows.find((row) => getRowId(row.original) === highlightedRowId)?.id
      : undefined;

  const highlightedRowRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (highlightedRowId && highlightedRowRef.current) {
      highlightedRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [highlightedRowId, highlightedRowKey]);

  return (
    <table
      className={clsx(
        "w-full border-separate border-spacing-0",
        fixedLayout && "table-fixed",
      )}
      style={
        enableHorizontalScroll
          ? { minWidth: table.getCenterTotalSize() }
          : undefined
      }
    >
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className="bg-surface-foreground">
            {headerGroup.headers.map((header, colIndex) => (
              <th
                key={header.id}
                className={clsx([
                  "relative",
                  !insideScrollFrame && "border-t border-border-primary-base",
                  "px-3 py-2",
                  "text-start text-text-secondary",
                  !insideScrollFrame && "first:rounded-ss-lg first:border-s",
                  !insideScrollFrame && "last:rounded-se-lg last:border-e",
                  stickyColumn &&
                    colIndex === 0 &&
                    "sticky start-0 z-[3] bg-surface-foreground",
                  stickyColumn &&
                    colIndex === 0 &&
                    isScrolled &&
                    FREEZE_SHADOW_CLASS,
                  header.column.columnDef.meta?.headerClassName ??
                    header.column.columnDef.meta?.className,
                ])}
                colSpan={header.colSpan}
                style={
                  enableHorizontalScroll
                    ? fixedLayout
                      ? { width: `${header.getSize()}px` }
                      : { minWidth: `${header.getSize()}px` }
                    : undefined
                }
              >
                <div className="flex items-center justify-between">
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  {header.column.columnDef.enableSorting !== false && (
                    <div onClick={header.column.getToggleSortingHandler()}>
                      <Button
                        size="ps-btn-sm"
                        type="button"
                        variant="ps-btn-transparent"
                        className="!px-1"
                      >
                        {defaultGetSortingIcon(header)}
                      </Button>
                    </div>
                  )}
                </div>
                <div
                  onDoubleClick={() => header.column.resetSize()}
                  onMouseDown={header.getResizeHandler()}
                  className={clsx([
                    "resizer group",
                    table.options.columnResizeDirection,
                    header.column.getIsResizing() && "isResizing",
                    colIndex === 0 && stickyColumn && "resizer-sticky",
                  ])}
                >
                  {colIndex === 0 && stickyColumn && (
                    <div
                      aria-hidden
                      className={clsx([
                        "absolute right-[3px] top-1/2 h-[18px] w-0.5 -translate-y-1/2 rounded-[1px] bg-stroke-primary transition-colors duration-[140ms] group-hover:bg-surface-brand-default",
                        header.column.getIsResizing() &&
                          "bg-surface-brand-default",
                      ])}
                    />
                  )}
                </div>
              </th>
            ))}
            {expandToggle && (
              <th
                className={clsx([
                  !insideScrollFrame && "border-t border-border-primary-base",
                  "w-10 px-3 py-2",
                  !insideScrollFrame && "last:rounded-se-lg last:border-e",
                ])}
              />
            )}
          </tr>
        ))}
      </thead>
      <tbody>
        {isError || rows.length === 0 ? (
          <tr aria-hidden="true">
            <td colSpan={columnCount} className="h-[360px]" />
          </tr>
        ) : (
          rows.map((row, index) => {
            const isHighlighted =
              !!highlightedRowId &&
              !!getRowId &&
              getRowId(row.original) === highlightedRowId;
            return (
              <tr
                key={row.id}
                ref={isHighlighted ? highlightedRowRef : undefined}
                className={clsx(
                  "hover:bg-background-table-hover",
                  stickyColumn && "group",
                  isHighlighted && "animate-row-highlight",
                  onRowClick && "cursor-pointer",
                  rowClassName,
                  getRowClassName?.(row.original),
                )}
                onClick={
                  onRowClick
                    ? (e) => {
                        if ((e.target as HTMLElement).closest("button, a")) {
                          return;
                        }
                        onRowClick(row.original);
                      }
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell, colIndex) => (
                  <td
                    key={cell.id}
                    className={clsx([
                      cell.column.columnDef.meta?.cellClassName ??
                        cell.column.columnDef.meta?.className,
                      "border-b border-border-primary-base",
                      "p-3 text-grey-900",
                      !insideScrollFrame && "first:border-s last:border-e",
                      !insideScrollFrame &&
                        index === table.getRowCount() - 1 &&
                        "first:rounded-bl-lg last:rounded-br-lg",
                      stickyColumn &&
                        colIndex === 0 &&
                        "sticky start-0 z-[4] bg-surface-primary before:pointer-events-none before:absolute before:inset-0 before:z-0 before:content-[''] group-hover:before:bg-background-table-hover",
                      stickyColumn &&
                        colIndex === 0 &&
                        isHighlighted &&
                        "before:bg-background-table-highlight",
                      stickyColumn &&
                        colIndex === 0 &&
                        isScrolled &&
                        FREEZE_SHADOW_CLASS,
                      cellContainerClassName,
                    ])}
                    style={
                      enableHorizontalScroll
                        ? fixedLayout
                          ? { width: `${cell.column.getSize()}px` }
                          : { minWidth: `${cell.column.getSize()}px` }
                        : undefined
                    }
                  >
                    <div
                      className={clsx(
                        stickyColumn &&
                          colIndex === 0 &&
                          "relative z-[1] w-full min-w-0 overflow-hidden",
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </div>
                  </td>
                ))}
                {expandToggle && (
                  <td
                    className={clsx([
                      "border-b border-border-primary-base",
                      "w-10 p-3 text-grey-900",
                      !insideScrollFrame && "last:border-e",
                      !insideScrollFrame &&
                        index === table.getRowCount() - 1 &&
                        "last:rounded-br-lg",
                    ])}
                  >
                    {row.getCanExpand() && (
                      <button
                        type="button"
                        aria-expanded={row.getIsExpanded()}
                        aria-label={
                          row.getIsExpanded()
                            ? expandToggle.getCollapseLabel(row.original)
                            : expandToggle.getExpandLabel(row.original)
                        }
                        onClick={row.getToggleExpandedHandler()}
                        className="flex items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-stroke-focused"
                      >
                        <BaseArrowDownIcon
                          aria-hidden="true"
                          className={clsx(
                            "h-4 w-4 shrink-0 text-icon-tertiary transition-transform",
                            row.getIsExpanded() && "rotate-180",
                          )}
                        />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
};
